<script setup>
  import { getTypeTag } from '~~/assets/js/utils'
  import { header } from '~~/store/header.js'
  import { sendMixpanelEvent } from '~/utils/host-browser-apis'

  const props = defineProps({
    tileWidth: {
      type: Number,
      default: 300,
    },
    tileHeight: {
      type: Number,
      default: 250,
    },
    imageHeight: {
      type: Number,
      default: 134,
    },
    captionWidth: {
      type: Number,
      default: 268,
    },
    data: {
      type: Object,
      default: null,
    },
    index: {
      type: Number,
      default: null,
    },
    isSummaryPage: {
      type: Boolean,
      default: false,
    },
    withNewscatcher: {
      type: Boolean,
      default: false,
    },
  })

  const tileWidth = props.tileWidth + 'px'
  const tileHeight = props.tileHeight + 'px'
  const imageHeight = props.imageHeight + 'px'
  const captionWidth = props.captionWidth + 'px'

  const sourceTopMargin = props.isSummaryPage ? '35px' : '8px'

  let summaryPageLink = `/summary?category=${props.data?.news_category}&aid=${props.data?.id}`
  const route = useRoute()
  const adProvider = route.query.native
  if (adProvider && props.withNewscatcher) {
    summaryPageLink += `&native=${adProvider}`
  }

  const type = getTypeTag()

  if (type && props.withNewscatcher) {
    summaryPageLink += `&type=${type}`
  }
  if (header.isNewHeader) {
    summaryPageLink += '&headerWithLogo=true'
  }

  const navigateToSummaryPage = async () => {
    await sendMixpanelEvent('NtpNewsTile_Clicked', {
      ntp_news_provider: props.data?.Provider,
      ntp_news_category: props.data?.news_category,
      ntp_tile_position: props.index,
    })
    const summaryUrl = props.withNewscatcher ? summaryPageLink : props.data?.TargetedUrl
    window.open(summaryUrl, '_blank')
  }

  const categoryLabel = useCategoryLabel(props.data?.news_category)

  const vNewsTile = {
    mounted: async () => {
      await sendMixpanelEvent('NtpNewsTile_Rendered', {
        ntp_news_category: props.data?.news_category,
        ntp_news_provider: props.data?.Provider,
      })
    },
  }
</script>

<template>
  <div class="tile">
    <a
      v-news-tile
      :href="withNewscatcher ? summaryPageLink : props.data?.TargetedUrl"
      target="_blank"
      @click.prevent="navigateToSummaryPage"
    >
      <div class="news" role="article" :aria-label="'News Article: ' + props.data?.Title || ''">
        <div class="news__image">
          <div class="category">
            <span class="category__label-text">{{ categoryLabel }}</span>
          </div>
          <img :src="props.data?.ThumbnailUrl" />
        </div>
        <div class="news__caption">
          <p class="text-inherit">
            {{ props.data?.Title || 'title' }}
          </p>
        </div>
        <div class="news__source">
          <div v-if="!props.isSummaryPage" class="news__favicon">
            <img :src="props.data?.ProvidersLogoUrl" />
          </div>
          <span class="news__provider" :class="{ news__newsweek: props.isSummaryPage }">
            {{ props.isSummaryPage ? `Sponsored | ${props.data.Provider}` : props.data?.Provider }}
          </span>
        </div>
      </div>
    </a>
  </div>
</template>

<style lang="scss" scoped>
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 6px 6px 0 0;
  }

  .category {
    height: 28px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    top: 0;
    right: 0;
    border-radius: 0 6px;
    position: absolute;
    padding: 6px 12px;
    background-color: rgba(0, 0, 0, 0.5);

    &__label-text {
      font-size: 12px;
      font-weight: 600;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.33;
      letter-spacing: normal;
      color: #fff;
    }
  }

  .news {
    --provider-color: #2f3438;
    --caption-color: #222629;
    --border-color: #dcdfe2;

    border-radius: 6px;
    width: v-bind(tileWidth);
    height: v-bind(tileHeight);
    border: 1px solid var(--border-color);
    overflow: hidden;

    &__image {
      position: relative;
      height: v-bind(imageHeight);
      width: v-bind(tileWidth);

      img {
        display: flex;
        align-items: center;
      }
    }

    &__caption {
      height: 60px;
      width: v-bind(captionWidth);
      margin: 16px 0 0 16px;
      align-self: stretch;
      flex-grow: 0;
      font-size: 16px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.25;
      letter-spacing: normal;
      text-align: left;
      color: var(--caption-color);
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;

      a:hover & {
        text-decoration: underline;
      }
    }

    &__favicon {
      width: 16px;
      height: 16px;
      flex-grow: 0;
      border-radius: 2px;
    }

    &__source {
      margin: v-bind(sourceTopMargin) 16px 0 16px;
      align-items: center;
      display: flex;
    }

    &__provider {
      margin-left: 8px;
      flex-grow: 0;
      font-size: 12px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 2;
      letter-spacing: normal;
      text-align: left;
      color: var(--provider-color);
      text-decoration: none;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      a:hover & {
        text-decoration: none;
      }
    }

    &__newsweek {
      margin-left: 0;
    }
  }

  .tile {
    width: v-bind(tileWidth);
    height: v-bind(tileHeight);
  }

  .dark .news {
    --provider-color: #9d9ea1;
    --caption-color: #9d9ea1;
    --border-color: #9d9ea133;
  }
  .dark a:has(.news) {
    color: #9d9ea1;
  }
</style>
